import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, Menu, X } from "lucide-react";
import PlugDLogo from "../../common/PlugDLogo";
import { HEADER_FLAT_LINKS, HEADER_MENUS, NavGroup } from "../navigation";

/**
 * Public header shared by every marketing page.
 *
 * Renders five megamenu dropdowns on desktop (Products, AI, Solutions,
 * Resources, plus the flat Pricing link) and a slide-down drawer on
 * mobile. Closes dropdowns on outside click, on Escape, and on route
 * change so the user never sees a stuck menu after navigating.
 */
export default function MarketingHeader() {
  const { t } = useTranslation();
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  // Close on route change — prevents "stuck-open" after a link click.
  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  // Close on outside click + Escape key.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) {
        return;
      }
      if (!containerRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <header
      ref={containerRef}
      className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-gray-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-gray-900 hover:opacity-80 transition-opacity"
            aria-label={t("mkt_header_home_aria", "PlugD home")}
          >
            <PlugDLogo size={28} className="rounded-lg" />
            <span className="font-bold text-lg">PlugD</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {HEADER_MENUS.map((group) => (
              <MenuDropdown
                key={group.labelKey}
                group={group}
                isOpen={openMenu === group.labelKey}
                onToggle={() =>
                  setOpenMenu((prev) => (prev === group.labelKey ? null : group.labelKey))
                }
              />
            ))}
            {HEADER_FLAT_LINKS.map((item) => (
              <Link
                key={item.labelKey}
                to={item.href}
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              >
                {t(item.labelKey, item.label)}
              </Link>
            ))}
          </nav>

          {/* Desktop auth buttons */}
          <div className="hidden lg:flex items-center gap-2">
            <Link
              to="/login"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
            >
              {t("mkt_header_login", "Log in")}
            </Link>
            <Link
              to="/register"
              className="px-4 py-2 text-sm font-semibold text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
            >
              {t("mkt_header_start_free", "Start free")}
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden p-2 text-gray-700"
            aria-label={
              mobileOpen
                ? t("mkt_header_close_menu", "Close menu")
                : t("mkt_header_open_menu", "Open menu")
            }
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && <MobileMenu />}
    </header>
  );
}

interface MenuDropdownProps {
  group: NavGroup;
  isOpen: boolean;
  onToggle: () => void;
}

function MenuDropdown({ group, isOpen, onToggle }: MenuDropdownProps) {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {t(group.labelKey, group.label)}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div
          className="absolute left-0 mt-2 w-80 rounded-xl shadow-xl bg-white border border-gray-200 p-3 z-50"
          role="menu"
        >
          {group.items.map((item) => (
            <Link
              key={item.labelKey}
              to={item.href}
              className="block rounded-lg px-3 py-2.5 hover:bg-gray-50 transition-colors group"
              role="menuitem"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">
                  {t(item.labelKey, item.label)}
                </span>
                {item.badge && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">
                    {item.badge}
                  </span>
                )}
              </div>
              {item.description && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {t(item.descriptionKey ?? "", item.description)}
                </p>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileMenu() {
  const { t } = useTranslation();
  return (
    <div className="lg:hidden border-t border-gray-200 bg-white max-h-[calc(100vh-4rem)] overflow-y-auto">
      <div className="px-4 py-4 space-y-6">
        {HEADER_MENUS.map((group) => (
          <div key={group.labelKey}>
            <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
              {t(group.labelKey, group.label)}
            </div>
            <div className="space-y-1">
              {group.items.map((item) => (
                <Link
                  key={item.labelKey}
                  to={item.href}
                  className="block px-2 py-1.5 text-sm font-medium text-gray-800 hover:text-gray-900"
                >
                  {t(item.labelKey, item.label)}
                </Link>
              ))}
            </div>
          </div>
        ))}
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
            {t("mkt_header_more", "More")}
          </div>
          {HEADER_FLAT_LINKS.map((item) => (
            <Link
              key={item.labelKey}
              to={item.href}
              className="block px-2 py-1.5 text-sm font-medium text-gray-800 hover:text-gray-900"
            >
              {t(item.labelKey, item.label)}
            </Link>
          ))}
        </div>
        <div className="pt-4 border-t border-gray-200 flex flex-col gap-2">
          <Link
            to="/login"
            className="w-full text-center px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg"
          >
            {t("mkt_header_login", "Log in")}
          </Link>
          <Link
            to="/register"
            className="w-full text-center px-4 py-2 text-sm font-semibold text-white bg-gray-900 rounded-lg"
          >
            {t("mkt_header_start_free", "Start free")}
          </Link>
        </div>
      </div>
    </div>
  );
}
